import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Eye, HeartHandshake, Compass, Bot, Workflow, Sparkles } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { FRAMEWORK } from "@/constants/testIds";
import { PILLARS, FRAMEWORK_BENEFITS, BUSINESS_OUTCOMES } from "@/data/pillars";

const PILLAR_ICONS = {
    "human-awareness": Eye,
    "understanding-others": HeartHandshake,
    "mindset-for-adaptability": Compass,
    "ai-partnership": Bot,
    "navigating-transformation": Workflow,
    "human-flourishing": Sparkles,
};

const Framework = () => {
    return (
        <div data-testid={FRAMEWORK.page}>
            {/* Header */}
            <section className="relative bg-indigo_brand text-white overflow-hidden">
                <div className="ih-container py-20 md:py-28 grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-end">
                    <div>
                        <p className="ih-eyebrow" style={{ color: "#08C020" }}>
                            The Framework
                        </p>
                        <h1 className="mt-3 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            HUMAN+ Framework
                            <span className="text-gold_brand">™</span>
                        </h1>
                        <p className="mt-7 font-opensans text-white/80 text-lg leading-relaxed max-w-2xl">
                            Six human-centered capability pillars that determine
                            whether AI strengthens your organization — or quietly
                            hollows it out. Every consulting engagement,
                            workshop, assessment, and future certification at
                            The Intuitus Hub™ is built on this framework.
                        </p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 max-w-md lg:justify-self-end">
                        {PILLARS.slice(0, 6).map((p) => (
                            <div
                                key={p.slug}
                                className="rounded-xl border border-white/15 bg-white/5 backdrop-blur-sm px-3 py-4"
                            >
                                <p className="font-poppins text-[10px] tracking-[0.18em] text-green_brand">
                                    {p.number}
                                </p>
                                <p className="font-montserrat font-bold text-white text-[13px] leading-tight mt-1">
                                    {p.name}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Overview */}
            <section className="ih-section bg-white">
                <div className="ih-container">
                    <SectionHeading
                        eyebrow="Framework Overview"
                        title="A research-informed model for the AI-enabled organization."
                        kicker="The HUMAN+ Framework™ defines the human capabilities that organizations must deliberately strengthen as AI takes on more cognitive work. It is the proprietary methodology behind The Intuitus Hub's™ consulting, workshops, assessments, and the HUMAN+ Foundations Experience™."
                    />
                </div>
            </section>

            {/* Six Pillars */}
            <section className="pb-8 md:pb-12 bg-white">
                <div className="ih-container">
                    <div className="grid gap-6 lg:grid-cols-2">
                        {PILLARS.map((p) => {
                            const Icon = PILLAR_ICONS[p.slug] || Sparkles;
                            return (
                                <article
                                    key={p.slug}
                                    data-testid={FRAMEWORK.pillar(p.slug)}
                                    className="ih-card p-8 md:p-10 relative overflow-hidden"
                                >
                                    <span
                                        className="absolute top-0 left-0 h-[4px] w-16 bg-green_brand"
                                        aria-hidden="true"
                                    />
                                    <div className="flex items-start gap-5">
                                        <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-indigo_brand/8 shrink-0">
                                            <Icon size={22} className="text-indigo_brand" />
                                        </span>
                                        <div>
                                            <p className="font-poppins font-medium text-green_brand text-[11px] tracking-[0.22em]">
                                                PILLAR {p.number}
                                            </p>
                                            <h3 className="mt-1 font-montserrat font-bold text-indigo_brand text-xl sm:text-2xl leading-snug">
                                                {p.name}
                                            </h3>
                                        </div>
                                    </div>

                                    <p className="mt-6 font-opensans text-[#4A5568] text-[15px] leading-relaxed">
                                        {p.definition}
                                    </p>

                                    <div className="mt-6 pt-6 border-t border-surface-border/60">
                                        <p className="font-raleway font-semibold text-indigo_brand text-xs uppercase tracking-[0.16em]">
                                            Outcomes for Your Organization
                                        </p>
                                        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                                            {p.outcomes.map((o) => (
                                                <li
                                                    key={o}
                                                    className="flex items-start gap-2 font-opensans text-sm text-[#4A5568]"
                                                >
                                                    <Check
                                                        size={15}
                                                        className="text-green_brand mt-[3px] shrink-0"
                                                    />
                                                    {o}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="ih-section bg-surface">
                <div className="ih-container">
                    <SectionHeading
                        eyebrow="Why This Framework"
                        title="Benefits"
                        kicker="Designed to be acted on — not just understood. Four reasons leadership teams choose the HUMAN+ Framework™."
                    />

                    <div className="mt-12 grid gap-6 sm:grid-cols-2">
                        {FRAMEWORK_BENEFITS.map((b) => (
                            <div key={b.title} className="ih-card p-7">
                                <p className="font-poppins font-medium text-green_brand text-[11px] tracking-[0.2em]">
                                    BENEFIT
                                </p>
                                <h3 className="mt-2 font-montserrat font-bold text-indigo_brand text-xl">
                                    {b.title}
                                </h3>
                                <p className="mt-3 font-opensans text-sm text-[#4A5568] leading-relaxed">
                                    {b.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Business Outcomes */}
            <section className="ih-section bg-white">
                <div className="ih-container grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
                    <div className="relative rounded-3xl overflow-hidden shadow-medium aspect-[4/5]">
                        <img
                            src="https://images.pexels.com/photos/7698712/pexels-photo-7698712.jpeg?auto=compress&cs=tinysrgb&w=1100"
                            alt="Diverse leaders in a strategy meeting"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <div>
                        <SectionHeading
                            eyebrow="Business Outcomes"
                            title="What changes when an organization gets HUMAN+ right."
                            kicker="The HUMAN+ Framework™ translates directly into the outcomes executive teams are actually measured on."
                        />
                        <ul className="mt-8 space-y-4">
                            {BUSINESS_OUTCOMES.map((o) => (
                                <li
                                    key={o}
                                    className="flex items-start gap-3 font-opensans text-[15px] text-indigo_brand"
                                >
                                    <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-green_brand/15 mt-0.5 shrink-0">
                                        <Check size={14} className="text-green_brand" />
                                    </span>
                                    {o}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="ih-section bg-surface">
                <div className="ih-container">
                    <div className="ih-card p-10 md:p-14 grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center shadow-medium">
                        <div>
                            <p className="ih-eyebrow">Apply the Framework</p>
                            <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl leading-tight">
                                See your organization&rsquo;s HUMAN+ Readiness
                                <span className="block text-gold_brand">
                                    across all six pillars.
                                </span>
                            </h2>
                            <p className="mt-5 font-opensans text-[#4A5568] text-base max-w-xl leading-relaxed">
                                The HUMAN+ Snapshot™ turns this framework into a
                                personalized Executive Report — your scorecard,
                                strengths, growth opportunities, and a clear
                                recommendation.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
                            <Link
                                to="/snapshot"
                                data-testid={FRAMEWORK.cta}
                                className="ih-btn-primary"
                            >
                                Take the FREE Snapshot
                                <ArrowRight size={16} />
                            </Link>
                            <Link to="/foundations" className="ih-btn-secondary">
                                Foundations Experience™
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Framework;
